import React, { useEffect, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface DeleteGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onDelete: () => Promise<void>;
    groupName: string;
    templateCount: number;
    assignmentCount: number;
}

export const DeleteGroupModal: React.FC<DeleteGroupModalProps> = ({
    isOpen,
    onClose,
    onDelete,
    groupName,
    templateCount,
    assignmentCount,
}) => {
    const [confirmed, setConfirmed] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmReady, setConfirmReady] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setConfirmReady(false);
            return;
        }
        setConfirmReady(false);
        const timer = window.setTimeout(() => setConfirmReady(true), 400);
        return () => window.clearTimeout(timer);
    }, [isOpen, groupName]);

    const handleDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await onDelete();
        } catch {
            setError('Failed to delete. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleClose = () => {
        setConfirmed(false);
        setError(null);
        onClose();
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={handleClose}
            header="Delete Template Group"
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonDisabled={deleting}
                        buttonOnClick={handleClose}
                    />
                    <Btn
                        variant="danger"
                        buttonText={deleting ? 'Deleting...' : 'Delete Group'}
                        buttonDisabled={!confirmed || deleting || !confirmReady}
                        buttonOnClick={handleDelete}
                    />
                </div>
            }
        >
                    {/* Warning Banner */}
                    <div className="zrl-warning-banner">
                        <span className="zrl-warning-icon">⚠️</span>
                        <span>
                            This will permanently delete the template group and remove it from all
                            assigned companies. This action cannot be undone.
                        </span>
                    </div>

                    {/* Details */}
                    <div className="zrl-delete-details">
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Group Name</span>
                            <span className="zrl-delete-detail-value">{groupName}</span>
                        </div>
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Templates</span>
                            <span className="zrl-delete-detail-value">{templateCount}</span>
                        </div>
                        <div className="zrl-delete-detail">
                            <span className="zrl-delete-detail-label">Assigned Companies</span>
                            <span className="zrl-delete-detail-value">{assignmentCount}</span>
                        </div>
                    </div>

                    {/* Confirmation Checkbox */}
                    <label className="zrl-confirm-checkbox" onClick={() => setConfirmed(!confirmed)}>
                        <input
                            type="checkbox"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className="zrl-confirm-text">
                            I understand this will permanently delete <strong>{groupName}</strong> and
                            remove it from all assigned companies.
                        </span>
                    </label>

                    {error && (
                        <div className="zrl-warning-banner" style={{ marginTop: 12 }}>
                            <span className="zrl-warning-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
        </CustomModal>
    );
};
